<template>
  <modal-card
    title="Select Field"
    fixed
    closeButton
    light
    @close="$emit('close')"
    class="group-field-select"
  >
    <div class="pt-4 pl-6 pr-6 fields-filters">
      <search-toggle-field
        v-model="search"
        placeholder="Search"
        alwaysOpen
        large
        dark
        autofocus
        :delay="500"
        class="mb-4"
      />

      <v-tabs
        v-if="!isVectorRecruiting"
        v-model="selectedFieldType"
        centered
        class="mb-0"
      >
        <v-tabs-slider color="appMain"></v-tabs-slider>

        <v-tab
          v-for="fieldType in filteredFieldTypes"
          :key="fieldType.value"
          :tab-value="fieldType.value"
          color="appMain"
        >
          {{ fieldType.text }}
        </v-tab>
      </v-tabs>
    </div>

    <div class="fields-list-wrap">
      <ul class="fields-list">
        <li
          class="fields-list__item"
          v-for="(field, idx) in fieldsList"
          :key="idx"
          @click="selectField(field)"
          :class="{ active: isFieldSelected(field) }"
        >
          <h5 v-if="field.header" class="appMain--text">
            {{ field.header }}
          </h5>
          <div class="fields-list__item-text">{{ field.text }}</div>
          <v-icon color="appMain" v-if="isFieldSelected(field)">
            check_circle
          </v-icon>
        </li>
      </ul>
    </div>

    <template #actions>
      <v-btn color="appMain" :disabled="!selectedField" @click="selectAndExit">
        Select
      </v-btn>
    </template>
  </modal-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SearchToggleField from '@/components/basic/search-toggle-field/SearchToggleField';
import { activities, eventsWithOptionalFields } from './groupHelpers';
import isEqual from 'lodash/isEqual';

export default {
  name: 'GroupFieldSelect',
  components: {
    SearchToggleField,
  },
  props: {
    filter: {},
    filterEvent: {},
    onlyFields: {
      type: Boolean,
    },
    filtersGroup: {},
    meta: {},
  },
  data() {
    return {
      search: '',
      selectedField: null,
      selectedFieldType: 'all',
      fieldTypes: [
        {
          text: 'All',
          value: 'all',
        },
        {
          text: 'Activities',
          value: 'activities',
          check: () => !this.isVectorRecruiting,
        },
        {
          text: 'Fields',
          value: 'attributes',
        },
      ],
      activityFields: [],
      activities,
    };
  },
  computed: {
    ...mapGetters('fields', [
      'attributesFields',
      'additionalFields',
      'eventFields',
    ]),
    filteredFieldTypes() {
      return this.fieldTypes.filter((type) => {
        if (type.check) {
          return type.check();
        }
        return true;
      });
    },
    showEventFields() {
      return (
        eventsWithOptionalFields.includes(this.filterEvent) &&
        this.filter.field.field !== 'event'
      );
    },

    fieldsList() {
      return this.filterFields();
    },

    allFields() {
      return this.attributesFields.concat(this.activitiesFields);
    },

    additionalActivitiesFields() {
      let activities = this.eventFields.map(({ field }) => field);
      return activities
        .filter((i) => {
          return !this.activities.some(({ value }) => value === i);
        })
        .map((a) => {
          return {
            value: a,
            text: this.toTitleCase(a),
            activity: true,
          };
        });
    },

    activitiesFields() {
      return [
        { group: 'activities', header: 'Activities' },
        ...this.activities,
        ...this.additionalActivitiesFields,
      ];
    },

    lessonId() {
      return this.getFieldValue('lesson');
    },

    lessonItemId() {
      return this.getFieldValue('item');
    },
  },
  methods: {
    ...mapActions(['loadLessonMeta']),
    ...mapActions('fields', { loadFields: 'load' }),
    toTitleCase(str) {
      return str
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    },
    filterFields() {
      let fields = [];
      if (this.showEventFields) {
        fields = this.activityFields;
      } else {
        fields = this[`${this.selectedFieldType}Fields`];
      }

      return fields.filter((field) => {
        const { type, text = '', value = '' } = field;
        if (type === 'secret') return false;

        if (this.search) {
          return this.compareStrings(`${text}-${value}`);
        }

        return true;
      });
    },
    compareStrings(text) {
      const regex = new RegExp(this.search, 'i');
      return text.match(regex);
    },
    isFieldSelected(field) {
      if (!this.selectedField) return;
      return isEqual(this.selectedField, field);
    },
    selectField(field) {
      if (field.header) return;

      if (this.selectedField && this.selectedField.value === field.value) {
        this.selectedField = null;
      } else {
        this.selectedField = field;
      }
    },
    selectAndExit() {
      this.$emit('select', this.selectedField);
      this.$emit('close');
    },
    setActivityFields() {
      if (!this.showEventFields || this.isVectorRecruiting) return;

      const lessonEvents = ['completed_lesson', 'started_lesson'];
      const lessonFields = ['campaign', 'trigger', 'item'];
      const eventsWithMeta = [
        'answered_question',
        'watched_video',
        'shared',
        'uploaded_video',
      ];

      if (lessonEvents.includes(this.filterEvent)) {
        return (this.activityFields = this.additionalFields.filter((i) => {
          return lessonFields.includes(i.field);
        }));
      }

      if (eventsWithMeta.includes(this.filterEvent)) {
        return this.setLessonMetaFields();
      }

      this.activityFields = this.additionalFields.filter((i) => i.is_property);
    },

    async setLessonMetaFields() {
      if (!this.lessonId) return;
      const answeredQuestionFields = ['trigger'];

      // add item field
      if (!this.findFilterInGroup('item')) {
        answeredQuestionFields.push('item');
      }

      // add fields from lesson item
      if (this.lessonItemId) {
        const items = await this.loadLessonMeta(this.lessonId);
        const lessonItem = items.find((i) => i.uuid === this.lessonItemId);
        if (lessonItem) {
          const keysToAdd = Object.keys(lessonItem.event_properties).filter(
            (key) => !this.findFilterInGroup(key)
          );
          answeredQuestionFields.concat(keysToAdd);
        }
      }

      return (this.activityFields = this.additionalFields
        .filter((i) => {
          return answeredQuestionFields.includes(i.field);
        })
        .sort((a, b) => {
          return (
            answeredQuestionFields.indexOf(a.field) -
            answeredQuestionFields.indexOf(b.field)
          );
        }));
    },

    findFilterInGroup(f) {
      if (!Array.isArray(this.filtersGroup)) return null;
      return this.filtersGroup.find((i) => i.field.field === f);
    },

    getFieldValue(f) {
      let fieldObj = this.findFilterInGroup(f);
      return fieldObj ? fieldObj.value.value || fieldObj.value : '';
    },
  },
  beforeDestroy() {
    this.activityFields = [];
  },
  mounted() {
    this.loadFields({ checkLoaded: true });

    if (!this.onlyFields) {
      this.setActivityFields();
    } else {
      this.selectedFieldType = 'attributes';
    }
  },
};
</script>

<style lang="less">
.fields-list-wrap {
  flex: 1;
  overflow: auto;
}

.group-field-select .fixed-modal__content.styled-modal__content {
  display: flex;
  flex-direction: column;
}

.fields-list {
  padding: 12px 16px !important;

  &__item {
    display: flex;
    height: 48px;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    cursor: pointer;

    &.active {
      background: #f2f2f3;
    }

    &:not(:last-child) {
      border-bottom: 2px solid #f2f2f3;
    }

    &-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.fields-filters {
  box-shadow: @content-shadow;
}
</style>
